// ARCHIVE page — питание/тренировки/вес by month, with a password gate for edits to
// locked/historical data. Ported verbatim from the original app's ARCHIVE section.
import { S, MEALS, MEAL_LABELS } from '../state.js';
import { save } from '../lib/sync.js';
import { fmtDate, fmtDateShort, monthLabel, shiftDate } from '../lib/dates.js';
import { dayKbzhu, wkStatusLine } from '../lib/calc.js';
import { escHtml, n1 } from '../lib/format.js';
import { IC, cicon } from '../lib/icons.js';
import { showConfirm } from '../components/confirmDialog.js';
import { requireArchivePassword } from '../components/passwordDialog.js';
import { openValuePrompt } from '../components/valuePrompt.js';
import { registerPage } from '../lib/router.js';
import { goToFoodDate } from './food.js';
import { weightOnOrBefore, toggleWeightVisibility } from './home.js';

let archTab = 'food';
let archMonth = null;

function archAvailableMonths() {
  let dates = [];
  if (archTab === 'food') dates = Object.keys(S.foodLog).filter(d => Object.values(S.foodLog[d] || {}).some(arr => arr && arr.length));
  else if (archTab === 'workout') dates = S.workoutLog.map(w => w.date);
  else if (archTab === 'weight') dates = S.weightLog.map(w => w.date);
  return [...new Set(dates.map(d => d.slice(0, 7)))].sort().reverse();
}

export function switchArchiveTab(t) {
  archTab = t;
  document.getElementById('archTabFood').classList.toggle('active', t === 'food');
  document.getElementById('archTabWk').classList.toggle('active', t === 'workout');
  document.getElementById('archTabWeight').classList.toggle('active', t === 'weight');
  const months = archAvailableMonths();
  if (!archMonth || !months.includes(archMonth)) archMonth = months[0] || null;
  renderArchive();
}

export function selectArchMonth(m) {
  archMonth = m;
  renderArchive();
}

export function renderArchive() {
  const months = archAvailableMonths();
  if (!archMonth || !months.includes(archMonth)) archMonth = months[0] || null;
  const bar = document.getElementById('archMonthBar');
  bar.innerHTML = months.length
    ? months.map(m => `<button class="btn btn-sm ${m === archMonth ? 'btn-primary' : 'btn-secondary'}" style="white-space:nowrap;flex-shrink:0" onclick="selectArchMonth('${m}')">${monthLabel(m)}</button>`).join('')
    : '';
  const panel = document.getElementById('archPanel');
  if (!archMonth) {
    panel.innerHTML = `<div class="empty"><div class="empty-icon">${cicon('list', 28)}</div><div class="empty-text">Пока нет данных</div></div>`;
    return;
  }
  if (archTab === 'food') renderArchFood(panel);
  else if (archTab === 'workout') renderArchWorkout(panel);
  else renderArchWeight(panel);
}
registerPage('archive', renderArchive);

function renderArchFood(panel) {
  const days = Object.keys(S.foodLog).filter(d => d.startsWith(archMonth) && Object.values(S.foodLog[d] || {}).some(arr => arr && arr.length)).sort().reverse();
  if (!days.length) { panel.innerHTML = `<div class="empty"><div class="empty-text">Нет записей за месяц</div></div>`; return; }
  panel.innerHTML = days.map(d => {
    const kb = dayKbzhu(d);
    const fl = S.foodLog[d];
    const isArch = S.archivedDays.includes(d);
    const mealsHtml = MEALS.map(m => {
      const items = fl[m] || [];
      if (!items.length) return '';
      return `<div style="margin-top:4px"><span style="font-size:11px;font-weight:700;color:var(--text3)">${MEAL_LABELS[m]}:</span> ${items.map(it => escHtml(it.name) + ' ' + (it.g || 0) + 'г').join(', ')}</div>`;
    }).join('');
    return `<div class="card" style="cursor:pointer" onclick="goToFoodDate('${d}')">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <div class="t-h3">${fmtDate(d)}</div>
        ${isArch ? `<span class="tag">${cicon('list', 12)}</span>` : ''}
      </div>
      <div style="margin-top:4px"><span class="tag tag-amber">${kb.k} ккал</span> <span style="font-size:11px;color:var(--text3)">Б ${kb.p} · Ж ${kb.f} · У ${kb.c}</span></div>
      <div style="font-size:12px;color:var(--text2);line-height:1.6">${mealsHtml}</div>
    </div>`;
  }).join('');
}

function renderArchWorkout(panel) {
  const entries = S.workoutLog.filter(w => w.date.startsWith(archMonth)).sort((a, b) => b.date.localeCompare(a.date));
  if (!entries.length) { panel.innerHTML = `<div class="empty"><div class="empty-text">Нет записей за месяц</div></div>`; return; }
  panel.innerHTML = entries.map(w => {
    const st = wkStatusLine(w);
    const meta = [];
    if (w.kcalBurned) meta.push(cicon('fire', 12) + ' ' + w.kcalBurned + ' ккал');
    if (w.steps) meta.push(cicon('shoe', 12) + ' ' + w.steps.toLocaleString() + ' шаг');
    if (w.duration) meta.push(w.duration + ' мин');
    const idx = S.workoutLog.indexOf(w);
    return `<div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <div class="t-h3">${fmtDate(w.date)}</div>
        <div style="display:flex;align-items:center;gap:8px">
          <span>${st.icon} ${st.text}</span>
          <button onclick="deleteArchWorkout(${idx})" class="dish-btn">${IC.trash}</button>
        </div>
      </div>
      ${meta.length ? `<div style="font-size:12px;color:var(--text3);margin-top:4px">${meta.join(' · ')}</div>` : ''}
      ${w.note ? `<div style="font-size:12px;color:var(--text2);margin-top:3px">${cicon('chat', 12)} ${escHtml(w.note)}</div>` : ''}
    </div>`;
  }).join('');
}

export function deleteArchWorkout(idx) {
  requireArchivePassword(() => {
    showConfirm('Удалить эту тренировку?', () => {
      S.workoutLog.splice(idx, 1);
      save(true);
      renderArchive();
    });
  });
}

function renderArchWeight(panel) {
  const entries = S.weightLog.filter(w => w.date.startsWith(archMonth)).sort((a, b) => b.date.localeCompare(a.date));
  const hidden = !!S.settings.hideWeight;
  const eyeRow = `<div style="display:flex;justify-content:flex-end;margin-bottom:6px">
      <button class="btn-icon-toggle" onclick="toggleArchWeightVisibility()" style="background:none;border:none;cursor:pointer;padding:2px;color:var(--text3)" title="Скрыть/показать вес">${hidden ? IC.eyeOff : IC.eye}</button>
    </div>`;
  if (!entries.length) { panel.innerHTML = eyeRow + `<div class="empty"><div class="empty-text">Нет записей за месяц</div></div>`; return; }
  panel.innerHTML = eyeRow + entries.map(w => {
    // "За неделю" here — change vs. the closest logged weight ~7 days before THIS entry,
    // not vs. the previous row in the list (which could be days apart either way).
    const weekAgo = n1(weightOnOrBefore(shiftDate(w.date, -7)) - w.w);
    const weekLabel = weekAgo === 0 ? '' : (weekAgo > 0 ? `▼ ${weekAgo} кг за неделю` : `▲ +${Math.abs(weekAgo)} кг за неделю`);
    return `<div class="card" style="display:flex;justify-content:space-between;align-items:center;padding:12px 14px">
      <div>
        <div class="t-secondary">${fmtDate(w.date)}</div>
        ${weekLabel && !hidden ? `<div class="t-secondary" style="font-size:11px;margin-top:2px;color:${weekAgo > 0 ? 'var(--accent)' : 'var(--red)'}">${weekLabel}</div>` : ''}
      </div>
      <div style="display:flex;align-items:center;gap:10px">
        <div class="t-h3 t-num">${hidden ? '••••' : w.w + ' кг'}</div>
        <button onclick="editArchWeight('${w.date}')" class="dish-btn">${IC.edit}</button>
        <button onclick="deleteArchWeight('${w.date}')" class="dish-btn">${IC.trash}</button>
      </div>
    </div>`;
  }).join('');
}

export function toggleArchWeightVisibility() {
  toggleWeightVisibility();
  renderArchive();
}

export function editArchWeight(date) {
  requireArchivePassword(() => {
    const entry = S.weightLog.find(w => w.date === date);
    if (!entry) return;
    openValuePrompt('Новое значение веса (кг) для ' + fmtDateShort(date), entry.w, nv => {
      if (isNaN(nv) || nv < 30 || nv > 300) return;
      entry.w = nv;
      save(true);
      renderArchive();
    });
  });
}

export function deleteArchWeight(date) {
  requireArchivePassword(() => {
    showConfirm('Удалить запись веса за ' + fmtDateShort(date) + '?', () => {
      S.weightLog = S.weightLog.filter(w => w.date !== date);
      save(true);
      renderArchive();
    });
  });
}

export { goToFoodDate };
